import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Icon, Link, SelectChipStates, useTheme } from '@Sage/hoi-poi-ui';
import { BaseReactCell } from '@Sage/ssm-web5';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { getMarketingStatusHistoryCount } from 'services/MarketingStatusService';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { errorToast, successToast } from 'utils/toast';
import { logMarketingCampaignsEvent } from 'containers/campaigns/marketing/utils/marketingTracking';
import {
    buildMarketingStatusChipOptions,
    fetchMarketingStatusList,
    getMarketingStatusIdFromRow,
    MARKETING_STATUS,
    NOOP_SET_MARKETING_CONSENT,
} from './helpers';
import MarketingStatusHistoryModal from './MarketingStatusHistoryModal';
import MarketingStatusReadOnlyChip from './MarketingStatusReadOnlyChip';
import './styles.scss';

/** Infinite-scroll placeholder rows have no entity id yet — do not show a default status chip. */
function hasMarketingStatusRowData(data) {
    const id = data?.Id ?? data?.id;
    if (id == null || id === '') {
        return false;
    }
    const numericId = Number(id);
    return !Number.isNaN(numericId) && numericId > 0;
}

function resolveStatusIdFromLabel(label, options, allowedIds) {
    if (typeof label !== 'string' || !options?.length) {
        return null;
    }
    const trimmed = label.trim();
    const byLabel = options.find((o) => String(o.label).trim() === trimmed);
    if (byLabel != null && allowedIds.includes(byLabel.value)) {
        return byLabel.value;
    }
    return null;
}

function normalizeStatus(value, allowedIds, options, data) {
    const fromRow = getMarketingStatusIdFromRow(data);
    if (fromRow != null) {
        if (!allowedIds?.length) {
            return fromRow;
        }
        return allowedIds.includes(fromRow) ? fromRow : null;
    }

    if (value !== null && value !== undefined && value !== '') {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
            if (!allowedIds?.length) {
                return parsed;
            }
            return allowedIds.includes(parsed) ? parsed : null;
        }
        if (typeof value === 'string') {
            const byLabel = resolveStatusIdFromLabel(value, options, allowedIds);
            if (byLabel != null) {
                return byLabel;
            }
        }
    }

    const labelFromRow = data?.MarketingStatus ?? data?.marketingStatus;
    if (labelFromRow) {
        const byLabel = resolveStatusIdFromLabel(labelFromRow, options, allowedIds);
        if (byLabel != null) {
            return byLabel;
        }
    }

    return null;
}

/** B2 history API uses singular entityType; app config uses plural route names. */
function getHistoryEntityType(entity) {
    if (entity?.entity === COMPANIES.entity) {
        return 'account';
    }
    if (entity?.entity === CONTACTS.entity) {
        return 'contact';
    }
    return null;
}

function fetchHistoryCount(historyEntityType, entityId) {
    return getMarketingStatusHistoryCount({
        entityType: historyEntityType,
        entityId,
    });
}

const MarketingStatusSelect = memo(
    ({
        data,
        value,
        onChange,
        setMarketingConsent = NOOP_SET_MARKETING_CONSENT,
        entity = null,
        isReadOnly = false,
        showHistory = false,
        ...props
    }) => {
        const dispatch = useDispatch();
        const theme = useTheme();
        const [listRows, setListRows] = useState([]);
        const [historyCount, setHistoryCount] = useState(null);
        const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

        const entityId = useMemo(() => {
            if (data?.Id == null) {
                return NaN;
            }
            return Number(data.Id);
        }, [data?.Id]);

        const historyEntityType = useMemo(() => getHistoryEntityType(entity), [entity]);
        const canShowHistory = showHistory && historyEntityType;
        const finalHistoryCount = useMemo(() => historyCount ?? 0, [historyCount]);

        useEffect(() => {
            if (!canShowHistory || Number.isNaN(entityId)) {
                setHistoryCount(null);
                return undefined;
            }

            let cancelled = false;
            fetchHistoryCount(historyEntityType, entityId)
                .then((count) => {
                    if (!cancelled) {
                        setHistoryCount(count);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setHistoryCount(0);
                    }
                });

            return () => {
                cancelled = true;
            };
        }, [canShowHistory, entityId, historyEntityType]);

        useEffect(() => {
            fetchMarketingStatusList(dispatch)
                .then((rows) => setListRows(Array.isArray(rows) ? rows : []))
                .catch(() => setListRows([]));
        }, [dispatch]);

        const options = useMemo(
            () => buildMarketingStatusChipOptions(listRows, theme),
            [listRows, theme],
        );

        const allowedIds = useMemo(() => options.map((o) => o.value), [options]);

        const [localStatus, setLocalStatus] = useState(() =>
            normalizeStatus(value, allowedIds, options, data),
        );

        useEffect(() => {
            setLocalStatus(normalizeStatus(value, allowedIds, options, data));
        }, [value, allowedIds, options, data]);

        const handleChange = useCallback(
            async (opt) => {
                if (Number.isNaN(entityId)) {
                    return;
                }
                const previous = normalizeStatus(value, allowedIds, options, data);
                if (opt.value === previous) {
                    return;
                }
                setLocalStatus(opt.value);
                try {
                    await setMarketingConsent({
                        id: entityId,
                        marketingStatusId: opt.value,
                        row: data,
                    });
                    onChange?.(data, opt);
                    if (opt.value === MARKETING_STATUS.SUBSCRIBED) {
                        logMarketingCampaignsEvent({
                            submodule: 'marketingStatus',
                            functionality: 'subscribe',
                        });
                    }
                    if (canShowHistory) {
                        fetchHistoryCount(historyEntityType, entityId)
                            .then(setHistoryCount)
                            .catch(() => setHistoryCount(0));
                    }
                    if (setMarketingConsent !== NOOP_SET_MARKETING_CONSENT) {
                        successToast({
                            title: getLiteral(
                                'title_toast_marketing_campaigns_marketing_status_update_success',
                            ),
                            text: getLiteral(
                                'label_toast_marketing_campaigns_marketing_status_update_success_message',
                            ),
                        });
                    }
                } catch {
                    setLocalStatus(previous);
                    if (setMarketingConsent !== NOOP_SET_MARKETING_CONSENT) {
                        errorToast({
                            title: getLiteral(
                                'title_toast_marketing_campaigns_marketing_status_update_error',
                            ),
                            text: getLiteral(
                                'label_toast_marketing_campaigns_marketing_status_update_error_message',
                            ),
                        });
                    }
                }
            },
            [
                allowedIds,
                canShowHistory,
                data,
                entityId,
                historyEntityType,
                onChange,
                options,
                setMarketingConsent,
                value,
            ],
        );

        const selectedOption = useMemo(
            () => options.find((o) => o.value === localStatus),
            [options, localStatus],
        );

        const handleOpenHistory = useCallback((event) => {
            event.stopPropagation();
            logMarketingCampaignsEvent({
                submodule: 'marketingStatus',
                functionality: 'historyView',
            });
            setIsHistoryModalOpen(true);
        }, []);

        const handleCloseHistory = useCallback(() => {
            setIsHistoryModalOpen(false);
        }, []);

        if (!hasMarketingStatusRowData(data)) {
            return null;
        }

        return (
            <BaseReactCell data={data} {...props}>
                <div className="fm-marketing-status-cell">
                    {options.length > 0 && localStatus != null ? (
                        isReadOnly ? (
                            <MarketingStatusReadOnlyChip option={selectedOption} />
                        ) : (
                            <SelectChipStates
                                options={options}
                                value={localStatus}
                                onChange={handleChange}
                                title={getLiteral('label_marketing_campaigns_change_status')}
                            />
                        )
                    ) : null}
                    {canShowHistory ? (
                        <Link
                            type="caption"
                            className="fm-marketing-status-cell__history-link"
                            onClick={handleOpenHistory}
                        >
                            <Icon name="accessTime" color="currentColor" />
                            <span>
                                {getLiteralWithParameters('label_marketing_status_view_history', [
                                    finalHistoryCount,
                                ])}
                            </span>
                        </Link>
                    ) : null}
                    {canShowHistory ? (
                        <MarketingStatusHistoryModal
                            isOpen={isHistoryModalOpen}
                            onClose={handleCloseHistory}
                            entityType={historyEntityType}
                            entityId={entityId}
                            historyCount={finalHistoryCount}
                        />
                    ) : null}
                </div>
            </BaseReactCell>
        );
    },
);

MarketingStatusSelect.displayName = 'MarketingStatusSelect';

export default MarketingStatusSelect;
